import { useConnectors } from "@/features/Data/hooks/useConnectors";
import { Button, Flex, IconButton, Stack, Text, useDisclosure } from "@chakra-ui/react";
import { Column, v2 } from "@pufflig/prompt-studio-types";
import { Icon, Page } from "@pufflig/prompt-studio-ui";
import { useEffect } from "react";
import { Helmet } from "react-helmet";
import { useParams, useSearchParams } from "react-router-dom";
import { loadConnectorSchema } from "../state/actions/loadConnectorSchema";
import { selectTable } from "../state/actions/selectTable";
import { updateColumn } from "../state/actions/updateColumn";
import { updateTableDescription } from "../state/actions/updateTableDescription";
import { useSemanticModelStore } from "../state/SemanticModelProvider";
import { useSemanticModel } from "../state/useSemanticModel";
import { DataSchemaSidebar } from "../components/DataSchemaSidebar";
import { EditTableDescriptionModal } from "../components/EditTableDescriptionModal";
import { SemanticModelHeader } from "../components/SemanticModelHeader";
import { EditSchemaYamlModal } from "../components/EditSchemaYamlModal";
import { TableListPanel } from "../components/TableListPanel";
import { TablePreviewPanel } from "../components/TablePreviewPanel";
import { useOnSemanticModelChange } from "../hooks/useOnSemanticModelChange";

export const SemanticModelPage = () => {
  const { semanticModelId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const teamId = searchParams.get("team") || undefined;
  const { data: connectors } = useConnectors(teamId);

  const connectorRow = connectors?.find((c) => c.resource_id === semanticModelId);

  const connector: v2.ConnectorResource | null = connectorRow
    ? {
        resourceId: connectorRow.resource_id,
        connectorType: connectorRow.type,
        createdAt: connectorRow.created_at,
        name: connectorRow.name,
        description: connectorRow.description,
        resourceType: "connector",
        isReady: true,
      }
    : null;

  const isListView = useSemanticModel((s) => s.isListView);
  const selectedTable = useSemanticModel((s) => s.selectedTable);
  const selectedTableSchema = useSemanticModel((s) => s.selectedTableSchema);
  const selectedTableDescription = useSemanticModel((s) => s.selectedTableDescription);
  const tableData = useSemanticModel((s) => s.tableData);
  const isLoadingPreview = useSemanticModel((s) => s.isLoadingPreview);
  const dbSchema = useSemanticModel((s) => s.dbSchema);
  const {
    isOpen: isEditDescriptionOpen,
    onOpen: onOpenEditDescription,
    onClose: onCloseEditDescription,
  } = useDisclosure();
  const { isOpen: isYamlOpen, onOpen: onOpenYaml, onClose: onCloseYaml } = useDisclosure();

  useOnSemanticModelChange(connector?.resourceId);

  useEffect(() => {
    if (!connector) return;
    loadConnectorSchema(connector.resourceId);
  }, [connector?.resourceId]);

  useEffect(() => {
    useSemanticModelStore.setState({ semanticModel: (connectorRow as any)?.semantic_model || null });
  }, [connectorRow?.semantic_model]);

  useEffect(() => {
    const view = searchParams.get("view");
    if (view === "table") {
      useSemanticModelStore.setState({ isListView: false });
    } else if (view === "list") {
      useSemanticModelStore.setState({ isListView: true });
    }
  }, []);

  useEffect(() => {
    const table = searchParams.get("table");
    if (!table || !connector || !dbSchema) return;
    if (selectedTable === table) return;
    const [schema, t] = table.split(".");
    if (schema && t) {
      handleSelectTable(schema, t);
    }
  }, [connector, dbSchema]);

  useEffect(() => {
    if (!connector || !dbSchema) return;
    if (searchParams.get("table")) return;
    if (selectedTable) return;

    const schemaNames = Object.keys(dbSchema.schemas || {});
    if (schemaNames.length === 0) return;
    const firstSchema = schemaNames[0];
    const tables = {
      ...(dbSchema.schemas?.[firstSchema]?.tables || {}),
      ...(dbSchema.schemas?.[firstSchema]?.views || {}),
    };
    const tableNames = Object.keys(tables);
    if (tableNames.length === 0) return;

    handleSelectTable(firstSchema, tableNames[0]);
  }, [connector, dbSchema]);

  const handleSelectTable = async (schema: string, table: string) => {
    if (!connector) return;
    useSemanticModelStore.setState({ isListView: true });
    searchParams.set("table", `${schema}.${table}`);
    searchParams.set("view", "list");
    setSearchParams(searchParams.toString());
    await selectTable(connector.resourceId, schema, table);
  };

  const handleToggleView = () => {
    const next = !isListView;
    useSemanticModelStore.setState((state) => {
      state.isListView = next;
    });
    searchParams.set("view", next ? "list" : "table");
    setSearchParams(searchParams.toString());
  };

  const handleUpdateColumn = async (updated: Column) => {
    if (!connector) return;
    await updateColumn(connector.resourceId, updated);
  };

  const handleUpdateTableDescription = async (description: string) => {
    if (!connector) return;
    await updateTableDescription(connector.resourceId, description);
  };

  return (
    <Page>
      <Flex width="100%" flex="1" direction="column" height="100%" overflow="hidden">
        <Helmet>
          <title>Semantic Model - Prompt Studio</title>
        </Helmet>
        <SemanticModelHeader name={connector?.name} />
        <Flex flex="1" overflow="hidden">
          <DataSchemaSidebar connector={connector} onSelectTable={handleSelectTable} onOpenYaml={onOpenYaml} />
          <Flex direction="column" flex="1" overflow="hidden">
            {selectedTable && (
              <Flex paddingY="spacing-md" paddingX="spacing-lg" alignItems="center" justifyContent="space-between">
                <Stack>
                  <Text textStyle="label/L/medium">{selectedTable || "Table"}</Text>
                  <Flex alignItems="center" gap="spacing-xxs">
                    <Text textStyle="label/M/regular" maxWidth="20rem" wordBreak="break-word">
                      {selectedTableDescription || "No description"}
                    </Text>
                    <IconButton
                      aria-label="Edit table description"
                      size="xs"
                      variant="ghost"
                      icon={<Icon size="sm" name="edit" />}
                      onClick={onOpenEditDescription}
                    />
                  </Flex>
                </Stack>
                <Button
                  variant="outline"
                  leftIcon={<Icon size="sm" name={isListView ? "table" : "data-intiger"} />}
                  size="sm"
                  onClick={handleToggleView}
                >
                  {isListView ? "Show Table" : "Show List"}
                </Button>
              </Flex>
            )}
            {selectedTable && isListView && (
              <TableListPanel columns={selectedTableSchema} onUpdateColumn={handleUpdateColumn} />
            )}
            {selectedTable && !isListView && <TablePreviewPanel data={tableData} isLoading={isLoadingPreview} />}
            {!selectedTable && (
              <Flex flex="1" alignItems="center" justifyContent="center">
                <Text textStyle="label/M/regular" color="foreground.secondary">
                  Select a table to view its schema and data
                </Text>
              </Flex>
            )}
          </Flex>
        </Flex>
      </Flex>
      <EditTableDescriptionModal
        isOpen={isEditDescriptionOpen}
        onClose={onCloseEditDescription}
        description={selectedTableDescription}
        onSave={handleUpdateTableDescription}
      />
      <EditSchemaYamlModal isOpen={isYamlOpen} onClose={onCloseYaml} connectorId={connector?.resourceId} />
    </Page>
  );
};
